/**
 * Google Ads networks API — `/setting/ads-network` and `/setting/ads-placement`
 * on the px backend.
 *
 * AdMob, GameAdZone, Jungle, ... all serve through the Google Mobile Ads SDK,
 * so a network is a name plus its ad unit IDs per platform. One is active at a
 * time; the app reads its IDs as `adsSetting.googleAds` (profile API and the
 * public GET /ads/setting).
 *
 * The placement keys (`banner_leaderboard`, `native_home`, ...) are not fixed:
 * they are the admin's "Ad placement keys" list, and every network has every key.
 */

const baseUrl = process.env.REACT_APP_API_BASE_URL;

export const AD_PLATFORMS = [
  { key: "android", label: "Android" },
  { key: "ios", label: "iOS" },
];

/**
 * Same rule as the backend DTO. No format is imposed — AdMob `ca-app-pub-1/2`,
 * Ad Manager `/6499/app/banner`, plain numbers, UUIDs all fit — only whitespace,
 * quotes, backslash and `< >` are refused. Empty is allowed.
 */
export const AD_UNIT_PATTERN = /^[^\s<>"'`\\]*$/;

/** Same rule as the backend: letters, digits, `_`, starting with a letter, max 64. */
export const PLACEMENT_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/** Groups the form and lists show keys under, by the key's prefix. */
const GROUPS = [
  { prefix: "banner", label: "Banner" },
  { prefix: "native", label: "Native" },
  { prefix: "interstitial", label: "Interstitial" },
  { prefix: "reward", label: "Reward" },
  { prefix: "app", label: "App open" },
];
const OTHER = "Other";

export const groupOf = (key) => GROUPS.find((g) => key.toLowerCase().startsWith(g.prefix))?.label || OTHER;

/** `[{ label, keys }]` in group order, each group keeping the keys' own order; empty groups left out. */
export const groupKeys = (keys) =>
  [...GROUPS.map((g) => g.label), OTHER]
    .map((label) => ({ label, keys: keys.filter((k) => groupOf(k) === label) }))
    .filter((g) => g.keys.length);

/** Exactly `keys`, every value a trimmed string. */
export const unitsFor = (units, keys) =>
  Object.fromEntries(keys.map((k) => [k, typeof units?.[k] === "string" ? units[k].trim() : ""]));

/** Every string value of a pasted platform map, trimmed — any keys. */
const stringsOf = (units) =>
  Object.fromEntries(
    Object.entries(units && typeof units === "object" ? units : {})
      .filter(([, v]) => typeof v === "string")
      .map(([k, v]) => [k, v.trim()])
  );

/**
 * Read pasted ad unit JSON. Works with what people copy: the whole API response
 * (`{ "status": true, "data": { "googleAds": {...} } }`), `adsSetting`, a
 * `"googleAds": {...}` snippet with or without the outer braces, or just
 * `{ "android": {...}, "ios": {...} }`. Returns `{ android, ios }` or null.
 */
export const parseUnitsJson = (text) => {
  const raw = (text || "").trim().replace(/,\s*$/, "");
  if (!raw) return null;
  for (const candidate of [raw, `{${raw}}`, `{${raw}}}`, `${raw}}`]) {
    let parsed;
    try {
      parsed = JSON.parse(candidate);
    } catch {
      continue;
    }
    if (!parsed || typeof parsed !== "object") continue;
    const root =
      parsed.data?.googleAds || parsed.data?.adsSetting?.googleAds || parsed.adsSetting?.googleAds || parsed.googleAds || parsed;
    if (root && typeof root === "object" && (root.android || root.ios)) {
      return { android: stringsOf(root.android), ios: stringsOf(root.ios) };
    }
  }
  return null;
};

/**
 * A network's IDs as the JSON the app receives — `{ "googleAds": { android, ios } }`,
 * every key in order. "Paste from JSON" and "Import JSON" read it back as-is.
 */
export const toGoogleAdsJson = (network, keys) =>
  JSON.stringify(
    { googleAds: { android: unitsFor(network?.android, keys), ios: unitsFor(network?.ios, keys) } },
    null,
    2
  );

/**
 * Put text on the clipboard. Uses the Clipboard API, and falls back to a hidden
 * textarea where that is not available (plain-http admin, older browsers).
 * Resolves true when the text was copied.
 */
export const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea route
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const copied = typeof document.execCommand === "function" && document.execCommand("copy");
    document.body.removeChild(area);
    return !!copied;
  } catch {
    return false;
  }
};

/** How many IDs are filled in, across both platforms. */
export const filledCount = (network, keys) =>
  AD_PLATFORMS.reduce((sum, p) => sum + keys.filter((k) => (network?.[p.key]?.[k] || "").trim()).length, 0);

const request = async (method, path, body) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}/setting${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await res.json().catch(() => ({}));
  // The /setting routes answer failures with HTTP 200 and `success: false`.
  if (!res.ok || data.success === false || data.status === false) {
    throw new Error(data?.message || `Request failed with status ${res.status}`);
  }
  return data;
};

export const fetchAdsNetworks = () => request("GET", "/ads-network");
export const createAdsNetwork = (payload) => request("POST", "/ads-network", payload);
export const updateAdsNetwork = (id, payload) => request("PUT", `/ads-network/${id}`, payload);
export const toggleAdsNetwork = (id) => request("PATCH", `/ads-network/${id}/toggle`);
export const deleteAdsNetwork = (id) => request("DELETE", `/ads-network/${id}`);

export const fetchAdPlacements = () => request("GET", "/ads-placement");
export const createAdPlacement = (key) => request("POST", "/ads-placement", { key });
export const renameAdPlacement = (id, key) => request("PUT", `/ads-placement/${id}`, { key });
export const deleteAdPlacement = (id) => request("DELETE", `/ads-placement/${id}`);
